import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users, Video, CalendarDays, Loader2, Hourglass } from "lucide-react";
import api from "@/lib/api";
import { ScheduleList, ProgressLine } from "@/components/programs/ProgramPanel";

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="eyw-card p-6" data-testid={testid}>
    <h2 className="font-serif text-xl text-[#1A242B] mb-4 flex items-center gap-2"><Icon size={18} className="text-[#D98E73]" /> {title}</h2>
    {children}
  </section>
);

export default function MyPrograms() {
  const { data, isLoading } = useQuery({ queryKey: ["my-programs"], queryFn: () => api.get("/me/programs").then((r) => r.data) });
  if (isLoading || !data) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-12 space-y-6" data-testid="my-programs-page">
      <div>
        <p className="eyw-label">Your journey</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A242B]">My Programs</h1>
        <p className="text-[#4A5D6B] mt-2">Every program you belong to, your upcoming sessions and the broadcasts waiting for you.</p>
      </div>
      <Card title="Active groups" icon={Users} testid="my-programs-groups">
        {data.groups.length === 0 ? (
          <p className="text-sm text-[#8FA89B]" data-testid="my-programs-empty">You're not in a program yet. Programs open when you buy an enhancer's package. <Link to="/enhancers" className="text-[#144F75] underline">Browse enhancers</Link></p>
        ) : (
          <ul className="space-y-4">
            {data.groups.map((g) => (
              <li key={g.id} data-testid={`my-program-${g.id}`}>
                <Link to={`/community/groups/${g.slug}`} className="text-[#144F75] font-medium hover:underline">{g.name}</Link>
                <p className="text-xs text-[#8FA89B] mb-1">{g.package_name}{g.host_name ? ` · with ${g.host_name}` : ""}</p>
                <ProgressLine p={g.progress} />
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Upcoming sessions" icon={CalendarDays} testid="my-programs-upcoming">
        <ScheduleList items={data.upcoming} testid="my-programs-upcoming-list" />
      </Card>
      <Card title="Broadcasts to watch" icon={Video} testid="my-programs-unwatched">
        <ScheduleList items={data.unwatched} testid="my-programs-unwatched-list" />
      </Card>
      {data.waitlist.length > 0 && (
        <Card title="Waitlists" icon={Hourglass} testid="my-programs-waitlist">
          <ul className="space-y-2 text-sm">
            {data.waitlist.map((w) => (
              <li key={w.id} data-testid={`my-waitlist-${w.id}`}>
                {w.package_name} — {w.status === "invited" ? <>a new group is open! <Link to={w.owner_type === "enhancer" ? `/enhancers/${w.owner_id}` : "/locations"} className="text-[#144F75] underline">Join now</Link></> : "you're on the waitlist"}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
